/*
 * BTM Interactions
 * IntersectionObserver for animations, glow timer,
 * haptic feedback, header scroll class, carousel pagination dots
 * No dependencies. Vanilla JS. Deferred load.
 * Updated: 2026-07-09
 */

(function () {
  'use strict';

  /* ─── INTERSECTION OBSERVER ──────────────────────────────── */
  // Adds .btm-animate-ready to elements when they enter the viewport.
  // CSS animations are paused until this class is present.

  var animatables = document.querySelectorAll(
    '.btm-arrive, .btm-emerge, .btm-heading-arrive, .btm-stagger'
  );

  if (animatables.length && 'IntersectionObserver' in window) {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('btm-animate-ready');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    animatables.forEach(function (el) { observer.observe(el); });
  } else {
    // Fallback: show everything immediately
    animatables.forEach(function (el) {
      el.classList.add('btm-animate-ready');
    });
  }

  /* ─── CTA GLOW TIMER ─────────────────────────────────────── */
  // Triggers the breathe pulse on primary CTAs after 3s.
  // Only fires once per page load.

  setTimeout(function () {
    document.querySelectorAll('.button--primary').forEach(function (btn) {
      btn.classList.add('btm-glow-ready');
    });
  }, 3000);

  /* ─── HEADER SCROLL CLASS ────────────────────────────────── */
  // Adds .btm-header-scrolled to the header section after 60px scroll.
  // CSS applies blur/dark background when class is present.

  var headerSection = document.querySelector('.shopify-section-header-sticky');

  if (headerSection) {
    var scrolled = false;
    var scrollThreshold = 60;

    window.addEventListener('scroll', function () {
      var past = window.scrollY > scrollThreshold;
      if (past !== scrolled) {
        scrolled = past;
        headerSection.classList.toggle('btm-header-scrolled', scrolled);
      }
    }, { passive: true });
  }

  /* ─── HAPTIC FEEDBACK ────────────────────────────────────── */
  // 8ms vibration on color swatch selection (Android only).
  // iOS provides its own native haptic on button :active.

  document.addEventListener('click', function (e) {
    var swatch = e.target.closest('.btm-kit__swatch, .color-swatch, [data-swatch]');
    if (swatch && navigator.vibrate) {
      navigator.vibrate(8);
    }
  });

  /* ─── CAROUSEL PAGINATION DOTS ──────────────────────────── */
  // Injects dot indicators below horizontal scroll carousels.
  // Updates active dot on scroll.

  function initCarouselDots(rail, dotContainer) {
    if (!rail || !dotContainer) return;

    var items = rail.children;
    if (items.length < 2) return;

    var dots = [];

    Array.from(items).forEach(function (_, i) {
      var dot = document.createElement('button');
      dot.className = 'btm-dots__dot' + (i === 0 ? ' btm-dots__dot--active' : '');
      dot.setAttribute('aria-label', 'Go to item ' + (i + 1));
      dot.addEventListener('click', function () {
        items[i].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      });
      dotContainer.appendChild(dot);
      dots.push(dot);
    });

    function btmStride() {
      if (items.length > 1) {
        var delta = items[1].offsetLeft - items[0].offsetLeft;
        if (delta > 0) return delta;
      }
      return items[0] ? items[0].offsetWidth : 1;
    }
    var itemWidth = btmStride();
    var ticking = false;
    window.addEventListener('resize', function () {
      itemWidth = btmStride();
    }, { passive: true });

    rail.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        var activeIndex = Math.round(rail.scrollLeft / (itemWidth || 1));
        dots.forEach(function (dot, i) {
          dot.classList.toggle('btm-dots__dot--active', i === activeIndex);
        });
        ticking = false;
      });
    }, { passive: true });
  }

  // Wire up review cloud rail
  var reviewRail = document.querySelector('.btm-review-cloud__rail');
  if (reviewRail) {
    var reviewDotsWrap = document.querySelector('.btm-review-cloud__dots');
    if (!reviewDotsWrap) {
      reviewDotsWrap = document.createElement('div');
      reviewDotsWrap.className = 'btm-dots';
      reviewRail.parentNode.insertBefore(reviewDotsWrap, reviewRail.nextSibling);
    }
    initCarouselDots(reviewRail, reviewDotsWrap);
  }

  // Wire up used-by rail
  var usedByRail = document.querySelector('.btm-used-by__rail');
  if (usedByRail) {
    var usedByDotsWrap = document.querySelector('.btm-used-by__dots');
    if (!usedByDotsWrap) {
      usedByDotsWrap = document.createElement('div');
      usedByDotsWrap.className = 'btm-dots';
      usedByRail.parentNode.insertBefore(usedByDotsWrap, usedByRail.nextSibling);
    }
    initCarouselDots(usedByRail, usedByDotsWrap);
  }

  /* ─── STAGGER INDEX ASSIGNMENT ───────────────────────────── */
  // Assigns --btm-i CSS custom property to each child of .btm-stagger
  // so the CSS animation-delay calc works correctly.

  document.querySelectorAll('.btm-stagger').forEach(function (container) {
    Array.from(container.children).forEach(function (child, i) {
      child.style.setProperty('--btm-i', i);
    });
  });

  /* ─── SCROLL DEPTH STICKY CTA ────────────────────────────── */
  // Shows sticky CTA only after hero CTA has scrolled out of view.
  // Hides it when user scrolls back up to hero.

  var heroCta = document.querySelector('.btm-hero__cta, .hero__cta, section:first-of-type .button--primary');
  var stickyCta = document.querySelector('.btm-sticky-cta, .btm-homepage-sticky-cta');

  if (heroCta && stickyCta && 'IntersectionObserver' in window) {
    var stickyObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          stickyCta.classList.toggle('btm-sticky-cta--visible', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );
    stickyObserver.observe(heroCta);
  }

  /* ─── PRODUCT IMAGE: prevent context menu on long-press ──── */
  document.querySelectorAll('.product__media img, .product-media-gallery img').forEach(function (img) {
    img.addEventListener('contextmenu', function (e) { e.preventDefault(); });
  });

})();
